import type { CaseCourtBindingProvenance } from '@lds-efsp/shared-contracts';

/**
 * The lookup fields the CaseCourtBinding is derived from — the two members
 * `attributeCaseCourt` (api-gateway `features/efiling/routes.ts`) writes onto
 * the `CaseDetail` it hands back, and nothing else.
 *
 * Declared structurally rather than imported: `CaseDetail` lives in the
 * api-gateway and the two apps share no module, so this narrow slice IS the
 * contract between them. It also keeps this module type-only on
 * `@lds-efsp/shared-contracts` (the same discipline `confirm-section.ts`
 * follows), so the SPEC-SOR-198 property probes under `internal/proptests` can
 * import this source directly and compose it with the real attributor instead
 * of re-modelling either side.
 */
export interface AttributedCaseCourt {
  /** The court the lookup asserted — absent when it asserted none. */
  courtCode?: string;
  /** WHICH SOURCE asserted it — absent when neither applied. */
  courtCodeProvenance?: CaseCourtBindingProvenance;
}

/**
 * The pair a draft persists about its attached case's court. Written together
 * and cleared together, always — never one without the other.
 */
export interface CaseCourtBindingFields {
  caseSourceCounty: string;
  caseSourceCountyProvenance: CaseCourtBindingProvenance | undefined;
}

/**
 * Derive the draft's CaseCourtBinding from what the case lookup asserted
 * (SPEC-SOR-198 R7/R17) — the single expression in the wizard that produces a
 * non-empty binding.
 *
 * SOR-182: PERSIST the court the lookup itself named — never synthesize one
 * from the county the lookup ran under. That county is a REQUEST parameter and
 * `?county=` selects a vendor SOAP endpoint rather than scoping the query to a
 * court, so stamping from it made the binding equal the filing court BY
 * CONSTRUCTION: `matches-court` was unfalsifiable and `foreign-court`
 * unreachable for any looked-up case. Reading it from the response is what
 * keeps Andrew's Madera-under-Marin repro detectable. That is why this function
 * takes ONLY the lookup response — the requested county is not a parameter it
 * could echo even by accident, which is the structural half of R17.
 *
 * SOR-191: the value and its PROVENANCE come from one expression, so they
 * cannot drift. The tag is what distinguishes a server-asserted binding from
 * the pre-SOR-182 filing-county echo that shares this string slot on drafts
 * saved before the repoint. Without it `caseCourtBinding` refuses to read a
 * court-matching value as `matches-court` — such a draft grace-passes as
 * `absent` instead of silently asserting same-court, while an echo naming a
 * DIFFERENT county still blocks.
 *
 * SOR-192/193: the provenance is the SERVER's assertion, carried verbatim and
 * re-derived from nothing here — this function contains no attribution tag of
 * its own. `attributeCaseCourt` names the case record when the record named its
 * own court, and the served court when it returned a nil `CaseCourt` and the
 * answering JTI endpoint supplied the identity (Madera does this on every
 * GetCase, so hardcoding a record tag here would mislabel that whole
 * population). It leaves the tag ABSENT when neither source applied — a Tyler
 * county with no served-court resolver — and that absence must survive the
 * write too: stamping a tag the server withheld would assert an attribution
 * nothing backs, re-opening the very hole SOR-191 closed. So the provenance
 * travels exactly as asserted, including not at all.
 *
 * An absent court code clears BOTH members: a value carrying a provenance tag
 * would claim the server asserted an empty court. `''` is the canonical empty
 * that `defaultWizardState` and `resetCourtScopedCaseData` both use, so no
 * reader needs a third representation of "no binding".
 *
 * Extracted from `CaseAttachPanel.handleCaseSelected` (SOR-211) so the rule is
 * reachable from a synchronous property witness. The decision does not move —
 * the attach is still the only caller, so R17's single-attributing-writer claim
 * is unaffected.
 */
export function stampCaseCourtBinding(caseDetail: AttributedCaseCourt): CaseCourtBindingFields {
  return {
    caseSourceCounty: caseDetail.courtCode ?? '',
    caseSourceCountyProvenance: caseDetail.courtCode ? caseDetail.courtCodeProvenance : undefined,
  };
}
